import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/elephant/constants';
import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { Grid2, Icon } from '@aibees/shared/components';
import {
  classnames,
  correctPhoneNumber,
  encodeQueryParamValues,
  peformanceLogTime,
} from '@aibees/shared/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { memo, useCallback, useState } from 'react';
import { useDecodedQueryParamFilterValues } from '../../hooks/use-decoded-query-param-filter-values';
import { useRenderFilterContent } from '../../hooks/use-render-filter-content';
import { FieldValidationType } from '../../types';
import { renderInput, renderTags } from '../../utils/render-utils';
import { parseCommaSeparatedValues } from '../autocomplete-tree-filter/utils';
import { FilterExactMatch } from '../filter-exact-match';
import { TextFieldAccordionFilterError } from '../text-field-accordion-filter/components/text-field-accordion-filter-error';
import { useTextFieldFilterError } from './hooks/use-text-field-filter-error';
import * as SX from './sx';
import type { TextFieldFilterProps as Props } from './types';

const ACCORDION_SLOT_PROPS = {
  transition: { unmountOnExit: true },
};

export const TextFieldFilter = memo(
  ({ className, onChange, ...filter }: Props) => {
    const { setParam } = useSearchQueryFilter({
      select: [filter.field!],
    });
    const searchType = useSearchTypeFromRoute();
    const { track } = useTrackEvents();
    const [search, setSearch] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const selectedValues = useDecodedQueryParamFilterValues(filter.field!);
    const error = useTextFieldFilterError(filter, selectedValues);
    const [focused, setFocused] = useState(false);

    const handleChangeAdvance = () => {
      setOpen((prev) => !prev);
    };

    const onDelete = useCallback(
      (event: React.MouseEvent<HTMLDivElement>, value: string) => {
        const newValues = selectedValues.filter((v) => v !== value);
        if (onChange) {
          onChange(filter.field!, newValues, undefined);
        } else {
          setParam(filter.field!, encodeQueryParamValues(newValues, undefined));
        }
      },
      [selectedValues, onChange, setParam, filter.field]
    );

    const handleChange = useCallback(() => {
      if (!search) return;
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change start: ${filter.field}`);

      const parsedValues = parseCommaSeparatedValues(search);
      if (parsedValues.length === 0) return;

      track('FILTER', {
        filterName: filter.name,
        filterType: filter.type,
        filterValue: parsedValues,
        searchType,
      });

      let newValues = [...selectedValues, ...parsedValues];

      if (filter.metadata?.validation?.type === FieldValidationType.PHONE)
        newValues = newValues.map((x) => correctPhoneNumber(x));

      if (!!filter.utils?.valueFormatter && filter.metadata?.delimiter) {
        newValues = newValues.map((x) =>
          String(filter.utils?.valueFormatter?.(x, filter.metadata?.delimiter))
        );
      }

      if (filter.metadata?.singleEntry)
        newValues.splice(0, newValues.length - 1);

      if (onChange) {
        onChange(filter.field!, newValues, search);
      } else {
        setParam(filter.field!, encodeQueryParamValues(newValues, undefined));
      }

      setSearch('');
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change end: ${filter.field}`);
    }, [
      search,
      filter.field,
      filter.metadata,
      filter.name,
      filter.type,
      filter.utils,
      onChange,
      searchType,
      selectedValues,
      track,
      setParam,
    ]);

    const renderedTags = renderTags({
      tagsList: selectedValues,
      utils: filter.utils,
      metadata: filter.metadata,
      onDelete,
      error,
    });

    const renderedInput = renderInput({
      field: filter.field!,
      search,
      onChange: setSearch,
      onBlur: (e) => {
        setFocused(false);
        handleChange();
      },
      onFocus: () => setFocused(true),
      onKeyDown: (e) => {
        if (e.key === 'Enter') {
          handleChange();
        }
      },
      placeholder: filter.metadata?.placeholder,
      error,
    });

    const advanceElements = filter.advance;

    const sourceItemsContent = useRenderFilterContent(
      advanceElements ?? [],
      open
    );

    return (
      <Box
        className={classnames('TextFieldFilter', className)}
        display={'flex'}
        flexDirection={'column'}
        gap={1}
      >
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <Card
            variant="outlined"
            color={error ? 'error' : focused ? 'primary' : 'default'}
          >
            <Grid2 container columns={48} display={'flex'}>
              <Grid2
                xs={error ? 42 : 48}
                display={'flex'}
                flexDirection={'column'}
              >
                {!!selectedValues.length && (
                  <CardContent padding="normal">{renderedTags}</CardContent>
                )}
                {renderedInput}
              </Grid2>
              <Grid2
                xs={error ? 6 : 0}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
              >
                <Icon name="info" color="error" size={18} />
              </Grid2>
            </Grid2>
          </Card>
          {error && (
            <TextFieldAccordionFilterError
              text={filter.metadata?.validation?.error ?? ''}
            />
          )}
          {advanceElements && (
            <Box width="100%">
              <Accordion
                className={classnames('FilterSection', className)}
                variant="filled"
                color="default"
                compact
                expanded={open}
                slotProps={ACCORDION_SLOT_PROPS}
              >
                <AccordionSummary
                  onClick={handleChangeAdvance}
                  expandIcon={
                    <Icon
                      size={16}
                      color={open ? 'primary' : 'default'}
                      name="chevron-down"
                    />
                  }
                  sx={SX.summary}
                >
                  <Typography
                    color={open ? 'primary' : 'default'}
                    variant="body4"
                  >
                    Source Settings
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={SX.details}>
                  {sourceItemsContent}
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Box>

        {filter.exactMatch?.enabled && (
          <FilterExactMatch
            field={filter.field!}
            label="Search Match"
            default={filter.exactMatch.default}
            customOptions={filter.exactMatch.customOptions}
          />
        )}
      </Box>
    );
  }
);

TextFieldFilter.displayName = 'TextFieldFilter';
